import type { Timestamp } from 'firebase/firestore';

/** How a number got into the app. Recorded per field, never per record. */
export type Source = 'photo' | 'manual' | 'voice';

export type CaptureMode = Source;
export type Instrument = 'zetasizer' | 'ribogreen' | 'unknown';
export type CaptureStatus = 'pending' | 'extracted' | 'confirmed' | 'rejected' | 'failed';
export type Confidence = 'high' | 'medium' | 'low';

/**
 * A single measured quantity with its provenance.
 * Deliberately generic: `unit` is a free string and the field keys live in
 * MEASUREMENT_FIELDS below, so adding a fifth measurement is a one-line change.
 */
export interface Measurement {
  value: number;
  sd: number | null;
  unit: string;
  source: Source;
  captureId: string | null;
}

/** The measurement keys a formulation can hold. v1 ships four. */
export const MEASUREMENT_KEYS = ['size', 'pdi', 'zeta', 'ee'] as const;
export type MeasurementKey = (typeof MEASUREMENT_KEYS)[number];

export interface FieldSpec {
  key: MeasurementKey;
  label: string;
  shortLabel: string;
  unit: string;
  instrument: Instrument;
  /** Excel number format for the value column. */
  numFmt: string;
  /** Decimal places used when rendering in the UI. */
  decimals: number;
  /** Plausible range. Outside this we warn — we never block. */
  sane: { min: number; max: number };
  /** Zeta is the only field where sign carries meaning that speech can drop. */
  signCritical?: boolean;
}

export const MEASUREMENT_FIELDS: Record<MeasurementKey, FieldSpec> = {
  size: {
    key: 'size',
    label: 'Z-average size',
    shortLabel: 'Size',
    unit: 'nm',
    instrument: 'zetasizer',
    numFmt: '0.0',
    decimals: 1,
    sane: { min: 20, max: 1000 },
  },
  pdi: {
    key: 'pdi',
    label: 'PDI',
    shortLabel: 'PDI',
    unit: '',
    instrument: 'zetasizer',
    numFmt: '0.000',
    decimals: 3,
    sane: { min: 0, max: 1 },
  },
  zeta: {
    key: 'zeta',
    label: 'Zeta potential',
    shortLabel: 'Zeta',
    unit: 'mV',
    instrument: 'zetasizer',
    numFmt: '0.00',
    decimals: 2,
    sane: { min: -100, max: 100 },
    signCritical: true,
  },
  ee: {
    key: 'ee',
    label: 'Encapsulation efficiency',
    shortLabel: 'EE',
    unit: '%',
    instrument: 'ribogreen',
    numFmt: '0.00',
    decimals: 2,
    sane: { min: 0, max: 100 },
  },
};

/** Which fields one capture of a given instrument can fill. */
export const INSTRUMENT_FIELDS: Record<Instrument, MeasurementKey[]> = {
  zetasizer: ['size', 'pdi', 'zeta'],
  ribogreen: ['ee'],
  unknown: [...MEASUREMENT_KEYS],
};

export interface UserProfile {
  email: string | null;
  displayName: string | null;
  createdAt: Timestamp | null;
  lastSeenAt: Timestamp | null;
}

/** Owner can delete and manage members; editor can capture and edit; viewer reads only. */
export type ProjectRole = 'owner' | 'editor' | 'viewer';

export interface ProjectMember {
  role: ProjectRole;
  email: string | null;
  displayName: string | null;
  addedAt: Timestamp | null;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  formulationCount: number;
  /** Who created it. Only they can delete it or change who else is on it. */
  ownerUid: string;
  /**
   * Flat list of member uids, owner included.
   * Duplicates the keys of `members` on purpose: Firestore can only run an
   * array-contains query against a flat array, and that query is what lets a
   * member find projects that are not under their own uid.
   */
  memberUids: string[];
  members: Record<string, ProjectMember>;
  /**
   * What this project records, chosen by its owner.
   *
   * Absent on projects created before readers were chosen per project; those
   * keep the fixed formulation table and the capturer's own readers. Present
   * (even empty) means the project is reader-driven: nothing can be captured
   * until at least one is chosen, and every capture mode reads against one.
   * Stored as full definitions so every editor captures with the same fields.
   */
  readers?: ProjectReader[];
  /** Words the spoken-note transcriber should expect: reagents, instruments, sample codes. */
  vocabulary?: string[];
}

/** A reader as a project carries it. */
export type ProjectReader = Pick<Reader, 'id' | 'name' | 'description' | 'fields'> & {
  builtIn?: boolean;
};

/**
 * Email to uid, so a project can be shared by typing an address.
 * Rules allow `get` but not `list`, so an address can only be resolved by
 * someone who already knows it — the same pattern as lab codes.
 */
export interface UserLookup {
  uid: string;
  email: string;
  displayName: string | null;
}

export interface Formulation {
  id: string;
  label: string;
  lipid: string;
  /**
   * Helper lipid and batch descriptor were dropped from the model on
   * 2026-09-19: in practice the helper rarely varies within a project, and both
   * were already spelled out in the formulation's own name, so they restated
   * the label in two more columns. Documents
   * written before that date still carry the fields; nothing reads them.
   */
  np: number | null;
  /**
   * What was changed about this formulation, in the user's own words.
   *
   * Sits next to N/P rather than with `notes` at the far end of the row,
   * because it answers "how does this one differ from the last one" — the
   * question asked while scanning the table, not while reading one record. Free
   * text on purpose: the modifications a lab makes are not a fixed list.
   */
  modification: string;
  measuredAt: Timestamp | null;
  size: Measurement | null;
  pdi: Measurement | null;
  zeta: Measurement | null;
  ee: Measurement | null;
  notes: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export interface Capture {
  id: string;
  mode: CaptureMode;
  storagePath: string | null;
  thumbnailPath: string | null;
  /**
   * Tokenised Storage URL for the original photo.
   * Storage rules cannot consult Firestore, so they cannot know who a project
   * is shared with. Persisting the download URL lets any member who can read
   * this document load the image, without opening the bucket to everyone.
   */
  downloadUrl: string | null;
  capturedByUid: string | null;
  capturedByName: string | null;
  transcript: string | null;
  instrument: Instrument;
  status: CaptureStatus;
  rawExtraction: ExtractionResult | null;
  confirmedData: ConfirmedData | null;
  appliedTo: string[];
  errorMessage: string | null;
  createdAt: Timestamp | null;
  confirmedAt: Timestamp | null;
}

/** One value+SD pair as the model returns it. */
export interface RawReadingValue {
  value: number | null;
  sd: number | null;
}

export interface ExtractionReading {
  sampleLabel: string | null;
  /** Formulation metadata the source may also carry — a table row usually does. */
  np?: number | null;
  modification?: string | null;
  /** ISO yyyy-mm-dd. */
  measuredAt?: string | null;
  size?: RawReadingValue | null;
  pdi?: RawReadingValue | null;
  zeta?: RawReadingValue | null;
  ee?: RawReadingValue | null;
}

export interface ExtractionResult {
  instrument: Instrument;
  confidence: Confidence;
  readings: ExtractionReading[];
  unreadableFields: string[];
  /** What each column heading was taken to mean — feeds the project glossary. */
  columnsSeen?: { heading: string; mappedTo: string | null }[];
  /** Present only when the model returned something we could not parse as JSON. */
  rawText?: string;
}

/** What the user actually approved on the confirmation screen. */
export interface ConfirmedData {
  readings: ConfirmedReading[];
}

export interface ConfirmedReading {
  formulationId: string | null;
  sampleLabel: string | null;
  values: Partial<Record<MeasurementKey, RawReadingValue | null>>;
  /** Fields the user explicitly marked as not measured. */
  notMeasured: MeasurementKey[];
}

export interface ExtractRequest {
  projectId: string;
  captureId: string;
  mode: 'photo' | 'voice';
  storagePath?: string;
  transcript?: string;
  instrument: Instrument;
}

export type ExtractResponse =
  | { ok: true; data: ExtractionResult }
  | { ok: false; error: string };

/**
 * One quantity a reader produces.
 *
 * `key` is what stored data is keyed by, so it is generated once from the label
 * and never changes afterwards — correcting "PDI " to "PdI" must not orphan a
 * year of readings.
 */
export interface ReaderField {
  key: string;
  label: string;
  /** Free text: nm, mV, %, RFU, or nothing at all. */
  unit: string;
  /** Decimal places for display and for the export's number format. */
  decimals: number;
  /** Plausible range. Outside it the app warns; it never blocks. */
  min: number | null;
  max: number | null;
  /**
   * Column headings that mean this field, fed to the extraction prompt.
   * A lab's own vocabulary beats anything guessable in advance.
   */
  aliases: string[];
}

/**
 * An instrument, as configured by the people using it.
 *
 * Replaces the hardcoded Instrument union: what a source produces is now data
 * on the project rather than a case in a switch, so a plate reader nobody
 * anticipated is a form to fill in rather than a code change.
 */
export interface Reader {
  id: string;
  name: string;
  description: string;
  fields: ReaderField[];
  /** Seeded by the app rather than created by a user. Editable regardless. */
  builtIn: boolean;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export interface ReaderDraft {
  name: string;
  description: string;
  fields: ReaderField[];
}
